import { LINKS } from "@/config/links";
import { useL } from "@/i18n";
import { trackCta, classifyCtaDestination, getPageSlug } from "@/lib/analytics";
import { useCtaVisibility } from "@/hooks/useSectionTracking";

// Detect external (http/https/mailto) links and apply target/rel so
// visitors keep the landing page open in their original tab.
const isExternal = (href = "") => /^(https?:|mailto:)/i.test(href);
const externalProps = (href) =>
  isExternal(href) ? { target: "_blank", rel: "noopener noreferrer" } : {};

// Internal hrefs are automatically prefixed with the active language
// (e.g. `/booking` → `/pt/booking` when browsing the Brazilian tree).
const useLocalizedHref = (href) => {
  const l = useL();
  return isExternal(href) ? href : l(href);
};

// Primary dark ink button
export const PrimaryCTA = ({ href = LINKS.bookCall, children, testId, className = "", location = null, articleSlug = null, contentId = null, targetAssetId = null, targetService = null }) => {
  const to = useLocalizedHref(href);
  const visibilityRef = useCtaVisibility(testId, { location, destination_type: classifyCtaDestination(href), page_slug: getPageSlug(), article_slug: articleSlug });
  return (
    <a
      href={to}
      ref={visibilityRef}
      data-testid={testId}
      {...externalProps(href)}
      onClick={() => trackCta({ ctaId: testId, location, href, articleSlug, contentId, targetAssetId, targetService })}
      className={`group inline-flex items-center gap-3 bg-ink text-paper px-8 py-4 font-body text-sm uppercase tracking-[0.18em] transition-colors duration-300 hover:bg-terracotta ${className}`}
    >
      <span>{children}</span>
      <svg
        className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-1"
        viewBox="0 0 24 24"
        fill="none"
        aria-hidden="true"
      >
        <path
          d="M4 12h16M14 6l6 6-6 6"
          stroke="currentColor"
          strokeWidth="1.8"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    </a>
  );
};

// Secondary outline button
export const OutlineCTA = ({ href = LINKS.diagnostic, children, testId, className = "", onClick, location = null, articleSlug = null, contentId = null, targetAssetId = null, targetService = null }) => {
  const to = useLocalizedHref(href);
  const visibilityRef = useCtaVisibility(testId, { location, destination_type: classifyCtaDestination(href), page_slug: getPageSlug(), article_slug: articleSlug });
  return (
    <a
      href={to}
      ref={visibilityRef}
      data-testid={testId}
      {...externalProps(href)}
      onClick={() => { trackCta({ ctaId: testId, location, href, articleSlug, contentId, targetAssetId, targetService }); onClick?.(); }}
      className={`group inline-flex items-center gap-3 border border-ink/30 text-ink px-8 py-4 font-body text-sm uppercase tracking-[0.18em] transition-colors duration-300 hover:border-terracotta hover:text-terracotta ${className}`}
    >
      <span className="pointer-events-none">{children}</span>
    </a>
  );
};

// Ghost / text link
export const TextCTA = ({ href = LINKS.process, children, testId, className = "", location = null, articleSlug = null, contentId = null, targetAssetId = null, targetService = null }) => {
  const to = useLocalizedHref(href);
  const visibilityRef = useCtaVisibility(testId, { location, destination_type: classifyCtaDestination(href), page_slug: getPageSlug(), article_slug: articleSlug });
  return (
    <a
      href={to}
      ref={visibilityRef}
      data-testid={testId}
      {...externalProps(href)}
      onClick={() => trackCta({ ctaId: testId, location, href, articleSlug, contentId, targetAssetId, targetService })}
      className={`group inline-flex items-center gap-2 font-body text-sm text-ink-soft underline decoration-terracotta/50 underline-offset-4 transition-colors duration-300 hover:text-terracotta ${className}`}
    >
      <span>{children}</span>
      <span className="transition-transform duration-300 group-hover:translate-x-1">&rarr;</span>
    </a>
  );
};

// Secondary "soft box" — a boxed CTA tinted with the surrounding
// background colour rather than a bold accent, so it reads as clearly
// clickable while staying visually quieter than PrimaryCTA. Pass `tone`
// to match the section it sits on: "light" for paper backgrounds (e.g.
// the Hero), "dark" for ink/dark backgrounds (e.g. the Final CTA).
export const SoftCTA = ({ href = LINKS.diagnostic, children, testId, className = "", location = null, tone = "light", articleSlug = null, contentId = null, targetAssetId = null, targetService = null }) => {
  const to = useLocalizedHref(href);
  const visibilityRef = useCtaVisibility(testId, { location, destination_type: classifyCtaDestination(href), page_slug: getPageSlug(), article_slug: articleSlug });
  const toneClasses =
    tone === "dark"
      ? "bg-paper/10 border border-paper/15 text-paper/90 hover:bg-paper/[0.16] hover:border-paper/25"
      : "bg-ink/[0.05] border border-ink/10 text-ink-soft hover:bg-ink/[0.08] hover:border-ink/15 hover:text-ink";
  return (
    <a
      href={to}
      ref={visibilityRef}
      data-testid={testId}
      {...externalProps(href)}
      onClick={() => trackCta({ ctaId: testId, location, href, articleSlug, contentId, targetAssetId, targetService })}
      className={`group inline-flex items-center gap-2 px-5 py-3 font-body text-sm transition-colors duration-300 ${toneClasses} ${className}`}
    >
      <span>{children}</span>
      <span className="transition-transform duration-300 group-hover:translate-x-1">&rarr;</span>
    </a>
  );
};
